import { createStore, useStore } from '@w/react-store'
import { useNativeNavigation } from './use-native-navigation'
/* oxlint-disable typescript/no-explicit-any -- React bindings with generic route types */
import * as React from 'react'
import { use, useEffectEvent } from 'react'
import type {
  AnyRoute,
  RouterContextRecord,
  RoutePaths,
  RouteParamsForPath,
  RouteSearchForPath,
  RouteLoaderForPath,
  RouterNavigateFn,
  NavigateOptionsForTree,
  NavigateOptions,
  SearchRecord,
} from './types'
import type { RegisteredRouter, AnyRegisteredRouter, Location } from './router'
import { matchRoutes, isRedirect, applyStateSchema } from './router'
import type { QueueCard, QueueRecord, QueueStore } from './queue'
import { cardParams, cardSearch, createQueueStore, sameCard } from './queue'
import { ReactPromise } from '@w/utils-fe'
import { BasicErrorBoundary } from '../../utils/utils-fe/src/basic-error-boundary'
import { withoutActWarning } from '@w/utils-fe'
import { createRouteOptionsApi } from './route-options'
import { getNavigation } from './navigation-polyfill'

const RouterReactContext = React.createContext<AnyRegisteredRouter | null>(null)

// Per-route error boundary - renders errorComponent if defined
type RouteErrorBoundaryProps = {
  errorComponent?: React.ComponentType<{ ctx: any; error: Error }>
  children: React.ReactNode
}

type RouteErrorBoundaryState = {
  error: Error | null
}

class RouteErrorBoundary extends React.Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  // Read ctx only when rendering the fallback. React's development error
  // profiler traverses props and would otherwise invoke resource getters.
  static override contextType = RouterReactContext
  declare context: React.ContextType<typeof RouterReactContext>

  constructor(props: RouteErrorBoundaryProps) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error }
  }

  override render() {
    const { error } = this.state
    const { errorComponent: ErrorComponent, children } = this.props

    if (error) {
      if (ErrorComponent) {
        return <ErrorComponent ctx={this.context?.ctx} error={error} />
      }
      // No errorComponent defined - rethrow to parent boundary
      throw error
    }

    return children
  }
}

type LinkProps<
  TRouteTree extends AnyRoute,
  TPath extends RoutePaths<TRouteTree>,
> = NavigateOptions<TRouteTree, TPath> & {
  children?: React.ReactNode
  preload?: boolean
  preloadDelay?: number
}

type LinkComponent<TRouteTree extends AnyRoute> = <
  TPath extends RoutePaths<TRouteTree>,
>(
  props: LinkProps<TRouteTree, TPath> &
    Omit<
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
      keyof LinkProps<TRouteTree, TPath> | 'href'
    >,
) => React.ReactElement | null
type MatchedRoute = {
  routeId: string
  params: Record<string, string>
  search: Record<string, unknown>
}

type RouterMatchContextValue = {
  loaderPromises: Map<string, ReactPromise<any>>
  matchedRoutes: MatchedRoute[]
  pendingRouteIds: Set<string> // Routes that have cache miss and are loading
  beforeLoadPendingRouteIds: Set<string>
  /**
   * First non-redirect beforeLoad rejection of this activity. The failing
   * route and everything below it render this error instead of a component:
   * a component would otherwise run on a ctx the hook never initialized and
   * mask the real cause with whatever it trips over first.
   */
  beforeLoadFailure?: { routeId: string; error: unknown }
}

function EmptyFallback() {
  return null
}

function ThrowRouteError({ error }: { error: unknown }): never {
  throw error
}

const RouterMatchContext = React.createContext<RouterMatchContextValue | null>(
  null,
)

/**
 * Activities are keyed by HISTORY ENTRY KEY, not by path. Once entry state
 * exists an activity is no longer a pure function of URL + loader cache: two
 * entries on the same URL carry different snapshots (§8, item 7).
 */
type ActivityState = {
  mode: 'hidden' | 'visible'
  matchContext: RouterMatchContextValue
  notFound: boolean
  /** pathname+search this activity was built for; identity is `entryKey`. */
  path: string
  entryKey: string
  /** Parsed snapshot of the entry's state — never read live from `navigation`. */
  entryState: unknown
}

function pruneOldActivities(
  activities: Map<string, ActivityState>,
  currentKey: string,
): Map<string, ActivityState> {
  const hiddenKeys = Array.from(activities.entries())
    .filter(
      ([key, activity]) => activity.mode === 'hidden' && key !== currentKey,
    )
    .map(([key]) => key)
  for (const key of hiddenKeys.slice(0, -2)) activities.delete(key)
  return activities
}

type ActivitiesContextValue = {
  removeActivity: (key: string) => void
  preloadPath: (path: string, location: Location) => void
}

type LocationStateContextValue = {
  state: unknown
  setState: (next: unknown) => void
}

const LocationStateContext =
  React.createContext<LocationStateContextValue | null>(null)

const ActivitiesContext = React.createContext<ActivitiesContextValue | null>(
  null,
)

const QueueContext = React.createContext<QueueStore | null>(null)

/** Location of a card: `to` filled with the card's params + its declared search. */
const cardNavigateOptions = (to: string, card: QueueCard) => ({
  to,
  params: cardParams(card),
  search: cardSearch(card),
})

const splitPath = (path: string): Location => {
  const [pathname, searchStr] = path.split('?')
  return { pathname, search: searchStr ? `?${searchStr}` : '' }
}


/**
 * Reducers are POSITIONAL so callers name the list in domain terms (`profiles`,
 * `flashcards`, `photos`) rather than through a fixed key.
 */
type QueueReducers<TCard extends QueueCard, TState> = {
  queue?: (cards: TCard[], current: TCard) => TCard[]
  /** INV-queue-reducers: state reducers read the same input snapshot regardless of reducer order. */
  queueState?: (state: TState, snapshot: { cards: TCard[]; current: TCard }) => TState
}

type QueueNavigateOptions<TCard extends QueueCard, TState> = QueueReducers<
  TCard,
  TState
> & {
  search?: SearchRecord
  hash?: string
}

type QueueApi<TCard extends QueueCard, TState> = {
  /** The sitting's deck identity, independent of the current URL. */
  id: string | undefined
  size: number
  /** The card designated by the CURRENT URL — not `cards[0]` (§4.3). */
  current: TCard | undefined
  /** `cards[1]`: the card an advance from the head lands on, and what prefetch warms. */
  next: TCard | undefined
  state: TState
  navigate: (options?: QueueNavigateOptions<TCard, TState>) => void
  update: (options: QueueReducers<TCard, TState>) => void
}

// Module-level OutletContext - shared across all binding calls (same as RouterReactContext)
const OutletContext = React.createContext<string | null>(null)

export function createRouterReactBindings<
  TRouter extends RegisteredRouter<AnyRoute, RouterContextRecord>,
>(router?: TRouter) {
  if (router)
    throw new Error(
      'createRouterReactBindings should not be called with a router instance. it is for types only.',
    )
  type RouteTree =
    TRouter extends RegisteredRouter<infer TRouteTree, any>
      ? TRouteTree
      : AnyRoute

  const useRouterInstance = () => {
    const contextRouter = React.useContext(RouterReactContext)
    const resolved = (router ??
      contextRouter) as TRouter extends RegisteredRouter<any, any>
      ? TRouter
      : AnyRegisteredRouter | null
    if (!resolved) {
      throw new Error('No router available for React bindings.')
    }
    return resolved
  }

  const useRouterMatchContext = () => {
    const ctx = React.useContext(RouterMatchContext)
    if (!ctx) {
      throw new Error(
        'useRouterMatchContext must be used within RouterProvider',
      )
    }
    return ctx
  }

  const useActivitiesContext = () => {
    const ctx = React.useContext(ActivitiesContext)
    if (!ctx) {
      throw new Error('useActivitiesContext must be used within RouterProvider')
    }
    return ctx
  }

  const resolveRouteByFrom = (
    routerInstance: AnyRegisteredRouter,
    from: string,
  ): AnyRoute => {
    if (from === '__root__') {
      return routerInstance.routeTree
    }
    const routesByToPath = routerInstance.types.routesByToPath as Record<
      string,
      AnyRoute | undefined
    >
    const route = routesByToPath[from]
    if (!route) {
      throw new Error(`Route path "${from}" not found in router.`)
    }
    return route
  }

  const useNavigate = (): RouterNavigateFn<RouteTree> => {
    const routerInstance = useRouterInstance()
    const [, startTransition] = React.useTransition()
    return React.useCallback(
      (opts: NavigateOptionsForTree<RouteTree>) => {
        startTransition(() => {
          routerInstance.navigate(opts)
        })
      },
      [routerInstance],
    )
  }

  const useParams = <TPath extends RoutePaths<RouteTree>>(options: {
    from: TPath
  }) => {
    const routerInstance = useRouterInstance()
    const route = React.useMemo(
      () => resolveRouteByFrom(routerInstance, options.from as string),
      [routerInstance, options.from],
    )
    const { matchedRoutes } = useRouterMatchContext()
    const match = matchedRoutes.find((m) => m.routeId === route.id)

    if (!match) {
      throw new Error(
        `useParams: route "${route.to}" is not matched for the current location.`,
      )
    }
    return match.params as RouteParamsForPath<RouteTree, TPath>
  }

  const useSearch = <TPath extends RoutePaths<RouteTree>>(options: {
    from: TPath
  }) => {
    const routerInstance = useRouterInstance()
    const route = React.useMemo(
      () => resolveRouteByFrom(routerInstance, options.from as string),
      [routerInstance, options.from],
    )
    const { matchedRoutes } = useRouterMatchContext()
    const match = matchedRoutes.find((m) => m.routeId === route.id)

    if (!match) {
      throw new Error(
        `useSearch: route "${route.to}" is not matched for the current location.`,
      )
    }
    return match.search as RouteSearchForPath<RouteTree, TPath>
  }

  const useLoaderData = <TPath extends RoutePaths<RouteTree>>(options: {
    from: TPath
  }) => {
    const routerInstance = useRouterInstance()
    const route = React.useMemo(
      () => resolveRouteByFrom(routerInstance, options.from as string),
      [routerInstance, options.from],
    )
    const { loaderPromises } = useRouterMatchContext()
    const promise = loaderPromises.get(route.id)

    if (!promise) {
      // No loader for this route
      return undefined as RouteLoaderForPath<RouteTree, TPath>
    }

    // use() will suspend if promise is pending, throw if rejected, return value if fulfilled
    return use(promise) as RouteLoaderForPath<RouteTree, TPath>
  }

  /**
   * Entry state (§5): a snapshot that lives on ONE history entry and rewinds
   * with back. Reads are activity-scoped. Writes go through
   * `navigation.updateCurrentEntry`, which patches that entry only — it is not
   * a navigation, so nothing re-matches and no loader re-runs.
   *
   * Declare `stateSchema` with required defaults on the route and the read type
   * is always `T`: a cold load yields defaults, so there is no "absent state"
   * branch for a consumer to forget.
   */
  const useLocationState = <TState = any,>(): [
    TState,
    (next: TState) => void,
  ] => {
    const ctx = React.useContext(LocationStateContext)
    if (!ctx) {
      throw new Error('useLocationState must be used within RouterProvider')
    }
    return [ctx.state as TState, ctx.setState]
  }

  /**
   * One queue per target route path, resolved by a single read keyed by the
   * exact-matched ranked-winner route for this activity's location (§4.5). No
   * pattern scan: `/files/$` and `/files/$fileId` both match `/files/a`, and a
   * matched chain contains every ancestor `fullPath`.
   *
   * Outside any queue the shape is empty (INV-6) — `current` still resolves
   * from the URL, so a cold load on a card URL renders.
   */
  const useQueue = <
    TCard extends QueueCard = QueueCard,
    TState = any,
  >(): QueueApi<TCard, TState> => {
    const routerInstance = useRouterInstance()
    const queueStore = React.useContext(QueueContext)
    const { matchedRoutes } = useRouterMatchContext()
    const [, startTransition] = React.useTransition()

    const winner = matchedRoutes.at(-1)
    const to = winner
      ? (
          routerInstance.types.routesById as Record<
            string,
            AnyRoute | undefined
          >
        )[winner.routeId]?.to
      : undefined

    const [recordStore] = React.useState(() => createStore<QueueRecord | null>(null))
    React.useLayoutEffect(() => {
      const update = () => { recordStore.update((queueStore && to ? queueStore.get(to) : undefined) ?? null) }
      const unsubscribe = queueStore?.subscribe(update)
      update()
      return unsubscribe
    }, [queueStore, to, recordStore])
    const record = useStore(recordStore)

    // Search granularity is uniform across a queue (INV-5), so one card decides
    // whether `current` carries search at all.
    const declaresSearch =
      record?.cards.some((card) => cardSearch(card) !== undefined) ?? false
    const current = winner
      ? ({
          ...winner.params,
          ...(declaresSearch ? { search: winner.search } : {}),
        } as TCard)
      : undefined

    const cards = (record?.cards ?? []) as TCard[]

    const applyReducers = (options: QueueReducers<TCard, TState>) => {
      let nextCards = cards
      let nextState = record?.state as TState
      if (options.queue) nextCards = options.queue(nextCards, current as TCard)
      if (options.queueState) nextState = options.queueState(nextState, { cards, current: current as TCard })
      return { nextCards, nextState }
    }

    const navigate = (options: QueueNavigateOptions<TCard, TState> = {}) => {
      if (!queueStore || !to || !record) return
      const { nextCards, nextState } = applyReducers(options)

      // §4.4 step 2 — departure: the head becomes the past. Resume is not a
      // branch, it is the absence of this pop.
      const departed = sameCard(nextCards[0], current)
        ? nextCards.slice(1)
        : nextCards
      queueStore.commit({ ...record, cards: departed, state: nextState })

      const head = departed[0]
      if (!head) return // step 3 — drained: stay put, counters stay readable
      startTransition(() => {
        routerInstance.navigate({
          to,
          params: cardParams(head),
          search: options.search ?? cardSearch(head),
          hash: options.hash,
        })
      })
    }

    const update = (options: QueueReducers<TCard, TState>) => {
      if (!queueStore || !record) return
      const { nextCards, nextState } = applyReducers(options)
      queueStore.commit({ ...record, cards: nextCards, state: nextState })
    }

    return {
      id: record?.id,
      size: cards.length,
      current,
      next: cards[1],
      state: record?.state as TState,
      navigate,
      update,
    }
  }

  type AnchorExtras = Omit<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    'href'
  >

  // Trigger preload by adding activity to shared ctx
  const TriggerPreload: React.FC<{
    navigateOptions: NavigateOptionsForTree<RouteTree>
  }> = ({ navigateOptions }) => {
    const routerInstance = useRouterInstance()
    const { preloadPath } = useActivitiesContext()

    const handlePreload = useEffectEvent(() => {
      const path = routerInstance.buildLocation(navigateOptions)
      const [pathname, searchStr] = path.split('?')
      const location: Location = {
        pathname,
        search: searchStr ? `?${searchStr}` : '',
      }
      preloadPath(path, location)
    })

    React.useEffect(() => {
      handlePreload()
    }, [])

    return null
  }

  const Link = function LinkComponent(
    props: LinkProps<RouteTree, RoutePaths<RouteTree>> & AnchorExtras,
  ) {
    const {
      to,
      params,
      search,
      hash,
      replace,
      children,
      preload = false,
      preloadDelay = 0,
      onClick,
      onMouseEnter,
      onFocus,
      ...rest
    } = props
    const routerInstance = useRouterInstance()
    const [, startTransition] = React.useTransition()
    const navigateOptions = React.useMemo(
      () =>
        ({
          to,
          params,
          search,
          hash,
          replace,
        }) as NavigateOptionsForTree<RouteTree>,
      [to, params, search, hash, replace],
    )
    const href = React.useMemo(
      () => routerInstance.buildLocation(navigateOptions),
      [routerInstance, navigateOptions],
    )

    const [isPreloading, setIsPreloading] = React.useState(false)
    const preloadTimeoutRef = React.useRef<ReturnType<
      typeof setTimeout
    > | null>(null)

    const handlePreload = React.useCallback(() => {
      if (!preload) return

      if (preloadTimeoutRef.current) {
        clearTimeout(preloadTimeoutRef.current)
      }

      if (preloadDelay > 0) {
        preloadTimeoutRef.current = setTimeout(() => {
          setIsPreloading(true)
        }, preloadDelay)
      } else {
        setIsPreloading(true)
      }
    }, [preload, preloadDelay])

    const handleCancelPreload = React.useCallback(() => {
      if (preloadTimeoutRef.current) {
        clearTimeout(preloadTimeoutRef.current)
        preloadTimeoutRef.current = null
      }
      setIsPreloading(false)
    }, [])

    React.useEffect(() => {
      return () => {
        handleCancelPreload()
      }
    }, [handleCancelPreload])

    const handleMouseEnter: React.MouseEventHandler<HTMLAnchorElement> = (
      event,
    ) => {
      onMouseEnter?.(event)
      handlePreload()
    }

    const handleMouseLeave: React.MouseEventHandler<HTMLAnchorElement> = () => {
      handleCancelPreload()
    }

    const handleFocus: React.FocusEventHandler<HTMLAnchorElement> = (event) => {
      onFocus?.(event)
      handlePreload()
    }

    const handleBlur: React.FocusEventHandler<HTMLAnchorElement> = () => {
      handleCancelPreload()
    }

    const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
      onClick?.(event)
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        (event.currentTarget.target && event.currentTarget.target !== '_self')
      ) {
        return
      }
      event.preventDefault()
      handleCancelPreload() // Stop preloading when navigating
      startTransition(() => {
        routerInstance.navigate(navigateOptions)
      })
    }

    const child = getOnlyLinkChild(children)
    // oxlint-disable-next-line jsx-a11y/anchor-has-content -- Template receives content below through cloneElement or the renderable child
    const anchor = <a
      {...rest}
      href={href}
      role={rest.role ?? 'link'}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
    let content: React.ReactElement
    if (React.isValidElement(child) && typeof child.type !== 'string') {
      if (!isRenderableLinkChild(child)) {
        throw new Error(
          `Link child ${getLinkChildName(child)} does not support root substitution. Mark the component with renderable = true.`,
        )
      }
      content = React.cloneElement(child, { render: anchor })
    } else content = React.cloneElement(anchor, undefined, child)

    return (
      <>
        {content}
        {isPreloading && <TriggerPreload navigateOptions={navigateOptions} />}
      </>
    )
  } as unknown as LinkComponent<RouteTree>

  const RouterProviderInner: React.FC<{
    matchedRoutes: MatchedRoute[]
    notFound: boolean
  }> = ({ matchedRoutes, notFound }) => {
    const routerInstance = useRouterInstance()
    const { beforeLoadPendingRouteIds, pendingRouteIds, beforeLoadFailure } =
      useRouterMatchContext()

    // oxlint-disable-next-line rc/preserve-manual-memoization -- React compiler false positive
    const content = React.useMemo(() => {
      // Handle not found
      if (notFound) {
        const rootRoute = routerInstance.routeTree
        const NotFoundComponent = (rootRoute.options as any).notFoundComponent
        if (NotFoundComponent) {
          return <NotFoundComponent />
        }
        return null
      }

      if (matchedRoutes.length === 0) {
        return null
      }

      // Find first renderable route. Pending components may render while
      // beforeLoad is running; normal components must wait for their own
      // beforeLoad and all ancestors' beforeLoad hooks.
      let hasBlockedAncestor = false
      for (let i = 0; i < matchedRoutes.length; i++) {
        const routeId = matchedRoutes[i].routeId

        const route = routerInstance.types.routesById[routeId]
        const options = route.options as any

        // If THIS route is pending and has pendingComponent, render it
        if (pendingRouteIds.has(routeId) && options.pendingComponent) {
          const PendingComponent = options.pendingComponent
          return (
            <OutletContext.Provider value={routeId}>
              <RouteErrorBoundary
                key={routeId}
                errorComponent={getErrorComponent(
                  routerInstance,
                  matchedRoutes,
                  routeId,
                )}
              >
                <PendingComponent ctx={routerInstance.ctx} />
              </RouteErrorBoundary>
            </OutletContext.Provider>
          )
        }

        if (beforeLoadFailure?.routeId === routeId) {
          return (
            <OutletContext.Provider value={routeId}>
              <RouteErrorBoundary
                key={routeId}
                errorComponent={getErrorComponent(
                  routerInstance,
                  matchedRoutes,
                  routeId,
                )}
              >
                <ThrowRouteError error={beforeLoadFailure.error} />
              </RouteErrorBoundary>
            </OutletContext.Provider>
          )
        }

        if (beforeLoadPendingRouteIds.has(routeId)) {
          hasBlockedAncestor = true
          continue
        }

        if (hasBlockedAncestor) {
          continue
        }

        // Skip root route - it should not have a component
        if (routeId === '__root__') continue

        // Render main component if has component
        if (options.component) {
          const Component = options.component
          return (
            <OutletContext.Provider value={routeId}>
              <RouteErrorBoundary
                key={routeId}
                errorComponent={getErrorComponent(
                  routerInstance,
                  matchedRoutes,
                  routeId,
                )}
              >
                <Component ctx={routerInstance.ctx} />
              </RouteErrorBoundary>
            </OutletContext.Provider>
          )
        }
      }

      return null
    }, [
      matchedRoutes,
      notFound,
      routerInstance,
      pendingRouteIds,
      beforeLoadPendingRouteIds,
      beforeLoadFailure,
    ])

    return content as React.ReactElement
  }

  const RouterProvider: React.FC<{
    router: TRouter
    wrapper?: React.ComponentType<{ children: React.ReactNode }>
    devtools?: React.ReactNode
    errorFallback?: React.ComponentType<{ error: Error; resetErrorBoundary?: () => void }>
  }> = ({ router: providedRouter, wrapper: Wrapper, devtools, errorFallback }) => {
    'use no memo' // React Compiler bug: extracts .map() callbacks to module scope, breaking closures
    const navigation = providedRouter.navigation ?? getNavigation()
    useNativeNavigation(navigation)
    const [activities, setActivities] = React.useState(
      new Map<string, ActivityState>(),
    )
    const loaderCacheRef = React.useRef(new Map<string, ReactPromise<any>>())
    // Hydrated at provider init, not on first useQueue(): prefetch runs before
    // any component mounts (§4.6).
    const [queueStore] = React.useState(createQueueStore)

    const [currentKey, setCurrentKey] = React.useState<string>(() => {
      return navigation.currentEntry?.key ?? 'initial'
    })
    // Paths already warmed by queue prefetch. Preloads no longer create
    // activities (they have no history entry, hence no key), so this is the
    // only record that a path is warm.
    const prefetchedRef = React.useRef(new Set<string>())

    // Activities ctx API. `addActivity` and `setActivityMode` are gone: neither
    // had a consumer, and neither survives activity identity by entry key.
    /**
     * `expected` guards against deleting someone else's activity. A `replace`
     * keeps the entry's key, so a redirect issued with `replace: true` lands its
     * destination under the SAME key as the activity that redirected. Without
     * the identity check, the redirected activity's cleanup deletes the
     * destination and the router renders nothing.
     */
    const removeActivity = React.useCallback(
      (key: string, expected?: ActivityState) => {
        withoutActWarning(() => {
          setActivities((prev) => {
            if (expected && prev.get(key) !== expected) return prev
            const next = new Map(prev)
            // oxlint-disable-next-line unicorn/no-immediate-mutation -- clone-then-mutate pattern is idiomatic for immutable state updates
            next.delete(key)
            return next
          })
        })
      },
      [],
    )

    const updateActivityLoaderPromise = React.useCallback(
      (key: string, routeId: string, promise: ReactPromise<any>) => {
        withoutActWarning(() => {
          setActivities((prev) => {
            const activity = prev.get(key)
            if (!activity) return prev
            const next = new Map(prev)
            const nextMatchContext: RouterMatchContextValue = {
              ...activity.matchContext,
              // oxlint-disable-next-line unicorn/no-immediate-mutation -- clone-then-mutate pattern is idiomatic for immutable state updates
              loaderPromises: new Map(activity.matchContext.loaderPromises),
            }
            // oxlint-disable-next-line unicorn/no-immediate-mutation -- clone-then-mutate pattern is idiomatic for immutable state updates
            nextMatchContext.loaderPromises.set(routeId, promise)
            // oxlint-disable-next-line unicorn/no-immediate-mutation -- clone-then-mutate pattern is idiomatic for immutable state updates
            next.set(key, {
              ...activity,
              matchContext: nextMatchContext,
            })
            return next
          })
        })
      },
      [],
    )

    /**
     * Birth (§4.2). Over an existing queue for the same `to` we KEEP EXISTING —
     * re-entering resumes the sitting, so a back-traverse past the origin after
     * activity GC cannot silently reset the counters. A drained record has no
     * head to resume onto, so it is re-born.
     *
     * Returns the navigate options for the head card, or undefined when the
     * fresh queue is empty — there is nowhere to go.
     */
    const birthQueue = React.useCallback(
      (opts: {
        to: string
        queue: QueueCard[]
        queueState?: unknown
        prefetch?: number
        queueId?: string
        search?: SearchRecord
        hash?: string
      }) => {
        const existing = queueStore.get(opts.to)
        // Resume only the SAME deck: a different `queueId` on one route is a
        // different sitting, not a re-entry (§6).
        const resumable =
          existing && existing.cards.length > 0 && existing.id === opts.queueId
        const record: QueueRecord = resumable
          ? existing
          : {
              to: opts.to,
              cards: opts.queue,
              state: opts.queueState,
              prefetch: opts.prefetch ?? 0,
              id: opts.queueId,
            }
        if (record !== existing) queueStore.commit(record)

        const head = record.cards[0]
        if (!head) return null
        return {
          to: opts.to,
          params: cardParams(head),
          search: cardSearch(head) ?? opts.search,
          hash: opts.hash,
        }
      },
      [queueStore],
    )

    type ActivityDefinition = {
      state: ActivityState
      readiness: Promise<void>[]
      /** Resolves once every background revalidation it started has settled. */
      startRevalidate: () => Promise<void>
      pendingRouteIds: Set<string>
      getBeforeLoadFailure: () => RouterMatchContextValue['beforeLoadFailure']
      wasRedirected: () => boolean
    }

    const buildActivityDefinition = React.useCallback(
      (
        path: string,
        location: Location,
        mode: 'hidden' | 'visible',
        options?: { ignoreRedirects?: boolean; entryKey?: string },
      ): ActivityDefinition => {
        // Activity identity. A preload has no history entry, so it gets a key
        // that can never collide with a real one and is never stored.
        const entryKey = options?.entryKey ?? `preload:${path}`
        const matched = matchRoutes(
          location,
          providedRouter.routeTree,
          providedRouter.metadata,
        )
        const routeTel = providedRouter.currentTel
        const loaderPromises = new Map<string, ReactPromise<any>>()
        const readiness: Promise<void>[] = []
        const revalidateStarters: Array<() => Promise<void>> = []
        const pendingRouteIds = new Set<string>()
        const beforeLoadPendingRouteIds = new Set<string>()
        const allowRedirects = !options?.ignoreRedirects
        let redirected = false
        // beforeLoad runs parent -> child. Loaders start in parallel after all
        // matched beforeLoad hooks complete.
        let beforeLoadSequence: Promise<
          { ok: true } | { ok: false; error: unknown }
        > = Promise.resolve({ ok: true })
        const beforeLoadResult: {
          failure?: RouterMatchContextValue['beforeLoadFailure']
        } = {}

        const handleRedirect = (error: unknown): boolean => {
          if (!isRedirect(error)) {
            return false
          }
          if (!allowRedirects) {
            removeActivity(entryKey)
            return false
          }
          // A rejected loader promise reaches here twice — once through
          // attachReadiness, once through its own rejection handler. Without
          // this guard the redirect is navigated twice and every loader on the
          // destination runs concurrently with itself.
          if (redirected) return true
          redirected = true
          {
            const redirectOpts = (error as any).options
            // Queue birth is committed where the redirect is HANDLED (§4.7):
            // preloads pass ignoreRedirects and never reach here, and
            // router.preload swallows redirects entirely.
            const destination = redirectOpts.queue
              ? birthQueue(redirectOpts)
              : redirectOpts
            if (!destination) return true
            navigation.navigate(providedRouter.buildLocation(destination), {
              history: redirectOpts.replace ? 'replace' : 'push',
            })
          }
          return true
        }

        const toReadiness = (promise: ReactPromise<any>) =>
          promise.then(
            // oxlint-disable-next-line promise/always-return -- intentional no-op on fulfillment; errors propagate via .catch
            () => {},
            (error) => {
              // If redirect was handled, don't propagate the error
              // The redirect triggers a new navigation
              handleRedirect(error)
            },
          )

        const attachReadiness = (promise: ReactPromise<any>) => {
          const ready = toReadiness(promise)
          readiness.push(ready)
        }

        const createRouteArgs = (
          match: (typeof matched.matches)[number],
          tel: typeof providedRouter.currentTel,
        ) => ({
          route: createRouteOptionsApi(
            match.route,
            match.params,
            match.search,
            match.exactMatch,
          ),
          params: match.params,
          search: match.search,
          ctx: providedRouter.ctx,
          tel,
        })

        const runRouteHook = async <T,>(input: {
          match: (typeof matched.matches)[number]
          name: 'route.beforeLoad' | 'route.loader'
          hook: (args: ReturnType<typeof createRouteArgs>) => T
        }): Promise<Awaited<T>> => {
          const result: { value?: Awaited<T>; redirect?: Error } = {}
          await routeTel.span(
            {
              name: input.name,
              displayName: input.match.route.fullPath ?? input.match.route.id,
              target: 'router',
              input: {
                params: input.match.params,
                search: input.match.search,
              },
              attributes:
                mode === 'hidden' ? { 'router.preload': true } : undefined,
            },
            async (tel) => {
              try {
                result.value = await input.hook(createRouteArgs(input.match, tel))
              } catch (error) {
                if (!isRedirect(error)) throw error
                result.redirect = error
              }
              return null
            },
          )
          if (result.redirect) throw result.redirect
          return result.value as Awaited<T>
        }

        for (const match of matched.matches) {
          const beforeLoad = (match.route.options as any)?.beforeLoad
          if (!beforeLoad) continue

          beforeLoadPendingRouteIds.add(match.route.id)
          if ((match.route.options as any)?.pendingComponent) {
            pendingRouteIds.add(match.route.id)
          }

          const beforeLoadInput = beforeLoadSequence.then((previous) => {
            if (!previous.ok) throw previous.error
            return runRouteHook({
              match,
              name: 'route.beforeLoad',
              hook: beforeLoad,
            })
          })
          const beforeLoadPromise = ReactPromise.wrap(
            Promise.resolve(beforeLoadInput),
          )
          beforeLoadSequence = beforeLoadInput.then(
            () => ({ ok: true }),
            (error: unknown) => {
              // Later hooks in the chain rethrow the same error; `??=` keeps
              // the route that actually failed.
              if (!isRedirect(error)) {
                beforeLoadResult.failure ??= { routeId: match.route.id, error }
              }
              return { ok: false, error }
            },
          )
          attachReadiness(beforeLoadPromise)
          beforeLoadPromise.then(
            () => {
              withoutActWarning(() => {
                setActivities((prev) => {
                  const activity = prev.get(entryKey)
                  if (!activity) return prev
                  const next = new Map(prev)
                  const beforeLoadPendingRouteIds = new Set(
                    activity.matchContext.beforeLoadPendingRouteIds,
                  )
                  beforeLoadPendingRouteIds.delete(match.route.id)
                  next.set(entryKey, {
                    ...activity,
                    matchContext: {
                      ...activity.matchContext,
                      beforeLoadPendingRouteIds,
                    },
                  })
                  return next
                })
              })
              return null
            },
            () => null,
          )
        }

        for (const match of matched.matches) {
          const loader = (match.route.options as any)?.loader
          if (!loader) continue

          const runLoader = () =>
            runRouteHook({ match, name: 'route.loader', hook: loader })

          const loaderCacheKey =
            match.route.id +
            JSON.stringify(match.params || {}) +
            JSON.stringify(match.search || {})
          const cached = loaderCacheRef.current.get(loaderCacheKey)
          if (cached && cached.status === 'fulfilled') {
            // Reuse fulfilled promise identity for Suspense stability. React
            // would suspend again on a new pending promise even when the route
            // already has usable data. Fresh data is loaded in the background.
            loaderPromises.set(match.route.id, cached)
            readiness.push(Promise.resolve())
            const revalidateBlocker = beforeLoadSequence
            revalidateStarters.push(() => {
              const revalidatePromise = ReactPromise.wrap(
                revalidateBlocker.then((previous) => {
                  if (!previous.ok) throw previous.error
                  return runLoader()
                }),
              )
              revalidatePromise.then(
                // oxlint-disable-next-line promise/always-return -- intentional no-op on fulfillment; errors propagate via .catch
                () => {
                  loaderCacheRef.current.set(loaderCacheKey, revalidatePromise)
                  updateActivityLoaderPromise(
                    entryKey,
                    match.route.id,
                    revalidatePromise,
                  )
                },
                (error) => {
                  if (handleRedirect(error)) {
                    return
                  }
                  loaderCacheRef.current.delete(loaderCacheKey)
                  updateActivityLoaderPromise(
                    entryKey,
                    match.route.id,
                    revalidatePromise,
                  )
                },
              )
              // Settled, not fulfilled: a rejected revalidation must not leave
              // the prefetch join hanging.
              return revalidatePromise.then(
                // oxlint-disable-next-line promise/always-return -- intentional side-effect-only .then; no value to return
                () => {},
                () => {},
              )
            })
          } else {
            const hasPendingComponent = !!(match.route.options as any)
              ?.pendingComponent
            if (hasPendingComponent) {
              pendingRouteIds.add(match.route.id)
            }
            const loaderInput = beforeLoadSequence.then((previous) => {
              if (!previous.ok) throw previous.error
              return runLoader()
            })
            const loaderPromise = ReactPromise.wrap(
              Promise.resolve(loaderInput),
            )
            loaderPromises.set(match.route.id, loaderPromise)
            attachReadiness(loaderPromise)
            loaderPromise.then(
              // oxlint-disable-next-line promise/always-return -- intentional no-op on fulfillment; errors propagate via .catch
              () => {
                loaderCacheRef.current.set(loaderCacheKey, loaderPromise)
              },
              (error) => {
                if (handleRedirect(error)) {
                  return
                }
                loaderCacheRef.current.delete(loaderCacheKey)
              },
            )
          }
        }

        // Entry state is snapshotted here, at build time, and never read live
        // from `navigation` by a component (§5). The commit precedes this
        // handler, so `currentEntry` is already the destination.
        const winner = matched.matches.at(-1)
        const state: ActivityState = {
          mode,
          matchContext: {
            loaderPromises,
            matchedRoutes: matched.matches.map((m) => ({
              routeId: m.route.id,
              params: m.params,
              search: m.search,
            })),
            pendingRouteIds,
            beforeLoadPendingRouteIds,
          },
          notFound: matched.notFound,
          path,
          entryKey,
          // A preload has no entry, so it has no state to snapshot.
          entryState:
            options?.entryKey && winner
              ? applyStateSchema(
                  winner.route,
                  navigation.currentEntry?.getState(),
                )
              : undefined,
        }

        return {
          state,
          readiness,
          pendingRouteIds,
          wasRedirected: () => redirected,
          getBeforeLoadFailure: () => beforeLoadResult.failure,
          startRevalidate: () =>
            Promise.all(revalidateStarters.map((start) => start())).then(
              // oxlint-disable-next-line promise/always-return -- intentional side-effect-only .then; no value to return
              () => {},
            ),
        }
      },
      [
        providedRouter,
        removeActivity,
        updateActivityLoaderPromise,
        birthQueue,
        navigation,
      ],
    )

    /**
     * A preload warms the loader cache and NOTHING else. It stores no activity:
     * activities are keyed by history entry key and a preload has no entry.
     * Nothing was lost — a stored preload activity was only ever reachable
     * through the traverse branch, which is exactly where it would have been
     * wrong to reuse it.
     */
    const preloadPath = React.useCallback(
      (path: string, location: Location) => {
        const { startRevalidate } = buildActivityDefinition(
          path,
          location,
          'hidden',
          { ignoreRedirects: true },
        )
        void startRevalidate()
      },
      [buildActivityDefinition],
    )

    /**
     * Prefetch on arrival (§4.7): preload cards[1 .. N] for depth N, clamped to
     * what the queue actually holds. Skips paths already warmed, so an arrival
     * does not re-run a loader that is already warm.
     */
    const runQueuePrefetch = useEffectEvent((location: Location) => {
      const matched = matchRoutes(
        location,
        providedRouter.routeTree,
        providedRouter.metadata,
      )
      if (matched.notFound) return
      const winner = matched.matches.at(-1)
      if (!winner) return
      const record = queueStore.get(winner.route.to)
      if (!record || record.prefetch <= 0) return

      for (const card of record.cards.slice(1, 1 + record.prefetch)) {
        const path = providedRouter.buildLocation(
          cardNavigateOptions(record.to, card),
        )
        if (prefetchedRef.current.has(path)) continue
        prefetchedRef.current.add(path)
        preloadPath(path, splitPath(path))
      }
    })

    const activitiesContextValue = React.useMemo<ActivitiesContextValue>(() => {
      return {
        removeActivity,
        preloadPath,
      }
    }, [removeActivity, preloadPath])

    // Use Effect Event to access current activities without making effect reactive to it
    const onNavigate = useEffectEvent((event: any) => {
      if (!event.canIntercept || event.navigationType === 'reload') return
      // A replace that changes only the URL fragment is page state (e.g. a debug panel's `#tel=…` link),
      // not a route change: leave activities and loaders alone. Compared by URL, not `event.hashChange`:
      // Chrome reports `history.replaceState` fragment changes as hashChange false. `navigate` fires before
      // commit, so `currentEntry` is still the source. An identical URL is not fragment-only and still routes.
      if (event.navigationType === 'replace' && _isFragmentOnlyChange(navigation.currentEntry?.url, event.destination?.url)) return

      // A later navigation supersedes this one; from that moment every write
      // this handler would make is on behalf of a location the user has left.
      // Without this the loser of a race clobbers the winner: navigate to a slow
      // route, navigate away before its loaders settle, and the slow route wins
      // when it finally lands.
      const signal: AbortSignal | undefined = event.signal
      const superseded = () => signal?.aborted === true

      event.intercept({
        async handler() {
          // Read the key SYNCHRONOUSLY, before any await: the commit has already
          // happened, so `currentEntry` is this navigation's destination — but
          // only until the next navigation commits.
          // `event.destination.key` is unusable here: it is '' for push and
          // replace, since a destination has no identity before it commits.
          const targetKey = navigation.currentEntry?.key ?? 'initial'

          let newLocation: { pathname: string; search: string }
          try {
            const url = new URL(event.destination.url)
            newLocation = { pathname: url.pathname, search: url.search }
          } catch {
            newLocation = { pathname: '/', search: '' }
          }
          const targetPath = newLocation.pathname + newLocation.search
          providedRouter._startNavigation(newLocation.pathname)

          // Only reuse an activity for back/forward (traverse). For push and
          // replace the entry is new, so its key cannot be in the map anyway.
          const isTraverse = event.navigationType === 'traverse'
          const existingActivity = isTraverse
            ? activities.get(targetKey)
            : undefined

          if (existingActivity) {
            // Activity already exists (back/forward navigation) - just flip mode!
            setActivities((prev) => {
              const next = new Map()
              // Hide all other activities
              // oxlint-disable-next-line unicorn/no-immediate-mutation -- clone-then-mutate pattern is idiomatic for immutable state updates
              for (const [key, activity] of prev.entries()) {
                if (key === targetKey) {
                  next.set(key, { ...activity, mode: 'visible' })
                } else {
                  next.set(key, { ...activity, mode: 'hidden' })
                }
              }
              return pruneOldActivities(next, targetKey)
            })
            setCurrentKey(targetKey)
            runQueuePrefetch(newLocation)
          } else {
            const {
              state,
              readiness,
              pendingRouteIds,
              startRevalidate,
              wasRedirected,
              getBeforeLoadFailure,
            } = buildActivityDefinition(targetPath, newLocation, 'visible', {
              entryKey: targetKey,
            })

            // Show pendingComponent if:
            // 1. At least one route with cache miss has pendingComponent
            // 2. NOT a search-only change on same route
            //
            // For search changes (same routes), keep old UI visible via React transition
            const currentActivity = activities.get(currentKey)
            const isSameRoutes =
              currentActivity &&
              currentActivity.matchContext.matchedRoutes.length ===
                state.matchContext.matchedRoutes.length &&
              currentActivity.matchContext.matchedRoutes.every(
                (r, i) =>
                  r.routeId === state.matchContext.matchedRoutes[i]?.routeId,
              )
            const shouldShowPending = pendingRouteIds.size > 0 && !isSameRoutes

            if (shouldShowPending) {
              // Route change with pendingComponent - show pending UI immediately
              setActivities((prev) => {
                // oxlint-disable-next-line unicorn/no-immediate-mutation -- clone-then-mutate pattern is idiomatic for immutable state updates
                const next = new Map()
                for (const [key, activity] of prev.entries()) {
                  next.set(key, { ...activity, mode: 'hidden' })
                }
                next.set(targetKey, state)
                return pruneOldActivities(next, targetKey)
              })
              setCurrentKey(targetKey)

              // When loaders complete, clear pendingRouteIds
              // oxlint-disable-next-line promise/always-return -- intentional side-effect-only .then; no value to return
              void Promise.all(readiness).then(() => {
                if (wasRedirected() || superseded()) {
                  removeActivity(targetKey, state)
                  return
                }
                withoutActWarning(() => {
                  setActivities((prev) => {
                    // oxlint-disable-next-line unicorn/no-immediate-mutation -- clone-then-mutate pattern is idiomatic for immutable state updates
                    const next = new Map(prev)
                    const activity = next.get(targetKey)
                    if (activity) {
                      next.set(targetKey, {
                        ...activity,
                        matchContext: {
                          ...activity.matchContext,
                          pendingRouteIds: new Set(),
                          beforeLoadPendingRouteIds: new Set(),
                          beforeLoadFailure: getBeforeLoadFailure(),
                        },
                      })
                    }
                    return next
                  })
                })
              })
              // The prefetch join is readiness AND the in-flight revalidates:
              // `readiness` does not contain revalidates, and joining on it
              // alone would let the next card's loader contend with this one's.
              void Promise.all(readiness)
                .then(startRevalidate)
                // oxlint-disable-next-line promise/always-return -- intentional side-effect-only .then; no value to return
                .then(() => {
                  if (superseded()) return
                  runQueuePrefetch(newLocation)
                })
            } else {
              // Has existing UI - wait for loaders while old UI stays visible
              await Promise.all(readiness)
              if (wasRedirected() || superseded()) return

              // Clear pendingRouteIds since we waited for loaders
              const finalState = {
                ...state,
                matchContext: {
                  ...state.matchContext,
                  pendingRouteIds: new Set(),
                  beforeLoadPendingRouteIds: new Set(),
                  beforeLoadFailure: getBeforeLoadFailure(),
                },
              }

              withoutActWarning(() => {
                setActivities((prev) => {
                  // oxlint-disable-next-line unicorn/no-immediate-mutation -- clone-then-mutate pattern is idiomatic for immutable state updates
                  const next = new Map()
                  for (const [key, activity] of prev.entries()) {
                    next.set(key, { ...activity, mode: 'hidden' })
                  }
                  next.set(targetKey, finalState)
                  return pruneOldActivities(next, targetKey)
                })
                setCurrentKey(targetKey)
              })
              // Same join as the pending branch: readiness is already awaited
              // above, so only the revalidates remain.
              void startRevalidate().then(
                // oxlint-disable-next-line promise/always-return -- intentional side-effect-only .then; no value to return
                () => {
                  if (superseded()) return
                  runQueuePrefetch(newLocation)
                },
              )
            }
          }
        },
      })
    })

    /**
     * In-place entry-state write (§5). `updateCurrentEntry` fires this with
     * `navigationType: null` — it is not a navigation, so it must patch that one
     * entry's snapshot and nothing else: no re-match, no loader re-run. Real
     * navigations fire it too (with a type); those are already handled by
     * `onNavigate`, which built the snapshot.
     */
    const onCurrentEntryChange = useEffectEvent((event: any) => {
      if (event.navigationType !== null) return
      const entry = navigation.currentEntry
      if (!entry) return
      setActivities((prev) => {
        const activity = prev.get(entry.key)
        if (!activity) return prev
        const winnerId = activity.matchContext.matchedRoutes.at(-1)?.routeId
        const winnerRoute = winnerId
          ? (
              providedRouter.types.routesById as Record<
                string,
                AnyRoute | undefined
              >
            )[winnerId]
          : undefined
        const next = new Map(prev)
        // oxlint-disable-next-line unicorn/no-immediate-mutation -- clone-then-mutate pattern is idiomatic for immutable state updates
        next.set(entry.key, {
          ...activity,
          entryState: winnerRoute
            ? applyStateSchema(winnerRoute, entry.getState())
            : entry.getState(),
        })
        return next
      })
    })

    // Set up Navigation API listener
    React.useEffect(() => {
      navigation.addEventListener('navigate', onNavigate)
      navigation.addEventListener('currententrychange', onCurrentEntryChange)

      // Trigger initial load. No synthetic key any more: the handler reads it
      // off `navigation.currentEntry`, which is already correct here.
      const initialEvent = {
        canIntercept: true,
        destination: { url: navigation.currentEntry.url },
        intercept: (options: any) => {
          void options.handler()
        },
      }
      onNavigate(initialEvent)

      return () => {
        navigation.removeEventListener('navigate', onNavigate)
        navigation.removeEventListener(
          'currententrychange',
          onCurrentEntryChange,
        )
      }
    }, [navigation])

    // isPending comes from useTransition - we'll inject it into ctx when rendering

    const activeActivity =
      activities.get(currentKey) ??
      Array.from(activities.values()).find(
        (activity) => activity.mode === 'visible',
      )
    const routeResetKey = activeActivity?.matchContext.matchedRoutes
      .map((match) => match.routeId)
      .join(',')

    // Entry state is read from the ACTIVITY, never live from `navigation`: the
    // entry commits before the intercept handler, so a live read would show the
    // destination's state to the component still rendering the old one.
    const activeEntryState = activeActivity?.entryState
    const locationStateValue = React.useMemo<LocationStateContextValue>(
      () => ({
        state: activeEntryState,
        setState: (next: unknown) => {
          navigation.updateCurrentEntry({ state: next })
        },
      }),
      [activeEntryState, navigation],
    )

    const routeContent = activeActivity ? (
      <RouterMatchContext.Provider value={activeActivity.matchContext}>
        <LocationStateContext.Provider value={locationStateValue}>
          <RouterProviderInner
            matchedRoutes={activeActivity.matchContext.matchedRoutes}
            notFound={activeActivity.notFound}
          />
        </LocationStateContext.Provider>
      </RouterMatchContext.Provider>
    ) : null

    return (
      <RouterReactContext.Provider value={providedRouter}>
        <QueueContext.Provider value={queueStore}>
          <ActivitiesContext.Provider value={activitiesContextValue}>
            {/* Before the first route commits there is no route to navigate away from: committing it is not a reset. */}
            <BasicErrorBoundary resetKeys={routeResetKey === undefined ? undefined : [routeResetKey]} fallback={errorFallback}>
              {Wrapper ? <Wrapper>{routeContent}</Wrapper> : routeContent}
            </BasicErrorBoundary>
            <BasicErrorBoundary fallback={EmptyFallback}>
              {devtools}
            </BasicErrorBoundary>
          </ActivitiesContext.Provider>
        </QueueContext.Provider>
      </RouterReactContext.Provider>
    )
  }

  const useRouter = () => useRouterInstance()

  type RouterContext =
    TRouter extends RegisteredRouter<any, infer TContext>
      ? TContext
      : RouterContextRecord

  const useRouterContext = (): RouterContext => {
    const routerInstance = useRouterInstance()
    return routerInstance.ctx as RouterContext
  }

  const OutletComponent: React.FC = () => {
    const routerInstance = useRouterInstance()
    const parentRouteId = React.useContext(OutletContext)
    const {
      beforeLoadPendingRouteIds,
      matchedRoutes,
      pendingRouteIds,
      beforeLoadFailure,
    } = useRouterMatchContext()

    // Find child route ID by parent position
    const currentIndex = parentRouteId
      ? matchedRoutes.findIndex((m) => m.routeId === parentRouteId)
      : -1
    const childRouteId = matchedRoutes[currentIndex + 1]?.routeId

    if (!childRouteId) {
      return null
    }

    const route = routerInstance.types.routesById[childRouteId]
    const options = route.options as any

    // If THIS route is pending and has pendingComponent, render it
    if (pendingRouteIds.has(childRouteId) && options.pendingComponent) {
      const PendingComponent = options.pendingComponent
      return (
        <OutletContext.Provider value={childRouteId}>
          <RouteErrorBoundary
            key={childRouteId}
            errorComponent={getErrorComponent(
              routerInstance,
              matchedRoutes,
              childRouteId,
            )}
          >
            <PendingComponent ctx={routerInstance.ctx} />
          </RouteErrorBoundary>
        </OutletContext.Provider>
      )
    }

    if (beforeLoadFailure?.routeId === childRouteId) {
      return (
        <OutletContext.Provider value={childRouteId}>
          <RouteErrorBoundary
            key={childRouteId}
            errorComponent={getErrorComponent(
              routerInstance,
              matchedRoutes,
              childRouteId,
            )}
          >
            <ThrowRouteError error={beforeLoadFailure.error} />
          </RouteErrorBoundary>
        </OutletContext.Provider>
      )
    }

    if (beforeLoadPendingRouteIds.has(childRouteId)) {
      return null
    }

    // Render main component if has component
    if (options.component) {
      const Component = options.component
      return (
        <OutletContext.Provider value={childRouteId}>
          <RouteErrorBoundary
            key={childRouteId}
            errorComponent={getErrorComponent(
              routerInstance,
              matchedRoutes,
              childRouteId,
            )}
          >
            <Component ctx={routerInstance.ctx} />
          </RouteErrorBoundary>
        </OutletContext.Provider>
      )
    }

    return null
  }
  const Outlet: React.FC = React.memo(OutletComponent)

  return {
    RouterProvider,
    useRouter,
    useRouterContext,
    useNavigate,
    useParams,
    useSearch,
    useLoaderData,
    useQueue,
    useLocationState,
    sameCard,
    Link,
    Outlet,
  }
}

function getOnlyLinkChild(children: React.ReactNode): React.ReactNode {
  const childArray = React.Children.toArray(children)
  if (childArray.length > 1) {
    throw new Error(
      `Link accepts at most one child, but received ${childArray.length}. Wrap the content in one element.`,
    )
  }

  const child = childArray[0]
  if (
    React.isValidElement<{ children?: React.ReactNode }>(child) &&
    child.type === React.Fragment
  )
    return getOnlyLinkChild(child.props.children)
  return child
}

function isRenderableLinkChild(
  child: React.ReactElement,
): child is React.ReactElement<{ render?: React.ReactElement }> {
  const type = child.type as { renderable?: boolean }
  return type.renderable === true
}

function getLinkChildName(child: React.ReactElement): string {
  const type = child.type
  if (typeof type === 'string') return type
  if (typeof type === 'function') {
    const namedType = type as { displayName?: string; name?: string }
    return namedType.displayName ?? namedType.name ?? 'component'
  }
  return 'component'
}

function getErrorComponent(
  router: AnyRegisteredRouter,
  matches: MatchedRoute[],
  routeId: string,
): RouteErrorBoundaryProps['errorComponent'] {
  const index = matches.findIndex((match) => match.routeId === routeId)
  for (let i = index; i >= 0; i--) {
    const route = router.types.routesById[matches[i].routeId]
    const { errorComponent } = route.options as Pick<
      RouteErrorBoundaryProps,
      'errorComponent'
    >
    if (errorComponent) return errorComponent
  }
  return undefined
}

function _isFragmentOnlyChange(from: string | undefined, to: string | undefined): boolean {
  if (!from || !to) return false
  const source = new URL(from)
  const destination = new URL(to)
  return source.hash !== destination.hash &&
    source.origin === destination.origin &&
    source.pathname === destination.pathname &&
    source.search === destination.search
}
